import { PROGRAM_PRESCRIBER_ID } from '../constants'
import { useGetAvailablePrescribers } from '@cannect/services/resources/scheduling'
import { useAvailableTimestamps } from '@cannect/hooks/useAvailableTimestamps'
import { cn } from '@cannect/lib/utils'
import { format, isSameDay } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Check } from 'lucide-react'
import { useMemo, useState } from 'react'
import { PrescriberAvatar, TrailHeading } from './screens'

interface ScheduleScreenProps {
  onSelect: (payload: {
    prescriberId: number
    prescriberUserId?: number
    duration?: number
    name: string
    date: string
    hour: string
    label: string
  }) => void
  selectedKey?: string
}

export const ScheduleScreen = ({ onSelect, selectedKey }: ScheduleScreenProps) => {
  // A consulta inclusa do programa é sempre com o prescritor configurado em VITE_PROGRAM_PRESCRIBER_ID.
  const { data, isLoading } = useGetAvailablePrescribers({
    filterBy: PROGRAM_PRESCRIBER_ID
      ? { type: 'prescribers_ids', value: JSON.stringify([PROGRAM_PRESCRIBER_ID]) }
      : undefined,
    sortBy: 'available_date_times',
    orderBy: 'asc'
  })

  const prescriber = data?.prescribers?.find((item) => Number(item.id) === PROGRAM_PRESCRIBER_ID)
  const timestamps = prescriber?.available_date_times || []
  const { isDateDisabled, getAvailableTimesForDate } = useAvailableTimestamps(timestamps)
  const [day, setDay] = useState<Date | null>(null)

  const availableDays = useMemo(() => {
    const unique: Date[] = []
    timestamps.forEach((stamp) => {
      const date = new Date(stamp)
      date.setHours(0, 0, 0, 0)
      if (!unique.some((item) => item.getTime() === date.getTime()) && !isDateDisabled(date)) {
        unique.push(date)
      }
    })
    return unique.sort((first, second) => first.getTime() - second.getTime())
  }, [timestamps, isDateDisabled])

  const activeDay = day || availableDays[0]
  const slots = activeDay ? getAvailableTimesForDate(activeDay) : []

  if (isLoading) {
    return <p className="text-sm text-[#6B6560]">Buscando horários inclusos no programa…</p>
  }

  if (!PROGRAM_PRESCRIBER_ID) {
    return <p className="text-sm text-[#6B6560]">A agenda do programa ainda não está configurada.</p>
  }

  if (!prescriber) {
    return (
      <p className="text-sm text-[#6B6560]">
        Não encontramos horários agora. Tente novamente em instantes ou fale com o atendimento.
      </p>
    )
  }

  const monthLabel = activeDay ? format(activeDay, "MMMM 'de' yyyy", { locale: ptBR }) : ''
  // Abreviação de 3 letras ('Seg', 'Ter'), como no design.
  const weekday = (date: Date) => {
    const name = format(date, 'EEEE', { locale: ptBR }).slice(0, 3)
    return name.charAt(0).toUpperCase() + name.slice(1)
  }

  return (
    <div className="flex flex-1 flex-col gap-5">
      <TrailHeading eyebrow="Agendamento" title="Escolha o dia e o horário da sua consulta" />
      <section className="rounded-2xl border border-solid border-[#ECECEC] bg-white p-4">
        <div className="flex items-center gap-3">
          <PrescriberAvatar name={prescriber.professional_name} />
          <div>
            <p className="m-0 text-sm font-semibold text-[#1A1A1A]">{prescriber.professional_name}</p>
            <p className="m-0 text-xs text-[#6B6560]">
              {prescriber.council}-{prescriber.uf_council} {prescriber.nr_council}
            </p>
            {prescriber.prescriber_expertises?.length ? (
              <p className="m-0 text-xs text-[#6B6560]">
                {prescriber.prescriber_expertises.map((item) => item.name).join(', ')}
              </p>
            ) : null}
          </div>
        </div>
        {prescriber.biography ? (
          <p className="m-0 mt-3 line-clamp-4 text-xs leading-snug text-[#433F3E]">{prescriber.biography}</p>
        ) : null}
        <hr className="my-3 border-0 border-t border-solid border-[#EEEEEE]" />
        <p className="m-0 inline-flex items-center gap-1.5 text-xs text-[#433F3E]">
          <Check className="size-3.5 text-marketplace-800" />
          Consulta médica inclusa no programa · pagamento confirmado
        </p>
      </section>

      <div className="flex flex-col gap-3">
        <p className="m-0 text-sm font-semibold text-[#1A1A1A] first-letter:uppercase">{monthLabel}</p>
        <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
          {availableDays.map((item) => {
            const selected = activeDay && isSameDay(item, activeDay)
            return (
              <button
                key={item.toISOString()}
                type="button"
                onClick={() => setDay(item)}
                className={cn(
                  'flex min-w-[52px] flex-1 flex-col items-center gap-0.5 rounded-xl border border-solid py-2.5',
                  selected
                    ? 'border-marketplace-800 bg-marketplace-800 text-white'
                    : 'border-[#E8E8E8] bg-white text-[#1A1A1A]'
                )}>
                <span className={cn('text-[11px]', selected ? 'text-white/85' : 'text-[#6B6560]')}>
                  {weekday(item)}
                </span>
                <span className="text-lg font-bold leading-none">{format(item, 'd')}</span>
              </button>
            )
          })}
        </div>
      </div>

      {activeDay ? (
        <div className="flex flex-col gap-3">
          <p className="m-0 text-sm font-semibold text-[#1A1A1A]">
            Horários disponíveis · {weekday(activeDay)}, {format(activeDay, 'd')}
          </p>
          <div className="grid grid-cols-3 gap-2">
            {slots.map((slot) => {
              const date = format(activeDay, 'yyyy-MM-dd')
              const key = `${prescriber.id}-${date}-${slot.value}`
              const active = selectedKey === key
              return (
                <button
                  key={slot.value}
                  type="button"
                  onClick={() =>
                    onSelect({
                      prescriberId: prescriber.id,
                      prescriberUserId: prescriber.user?.id,
                      duration: prescriber.appointment_duration,
                      name: prescriber.professional_name,
                      date,
                      hour: slot.value,
                      label: `Consulta médica, ${weekday(activeDay)}, ${format(activeDay, "d 'de' MMMM", { locale: ptBR })}, ${slot.label}`
                    })
                  }
                  className={cn(
                    'rounded-xl border border-solid py-3 text-sm',
                    active
                      ? 'border-marketplace-800 bg-marketplace-200 font-semibold text-[#1A1A1A]'
                      : 'border-[#E8E8E8] bg-white text-[#1A1A1A]'
                  )}>
                  {slot.label}
                </button>
              )
            })}
          </div>
        </div>
      ) : null}
    </div>
  )
}
